import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs/index';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import {
    BsModalConfirmComponent,
    ConfirmDialogResult,
    createConfirmModalOptions
} from '../../../core/modal/modal-confirm.component';
import { ReasonsSettingsActions } from '../../../reasons/state/reasons-settings.actions';
import {
    AppointmentListService } from '../../appointment-list/state/appointment-list.service';
import { AppointmentSettingsComponent } from '../../appointment-settings/appointment-settings.component';
import { SubscriptionsDialogComponent } from '../subscriptions-dialog.component';
import { PatientsActions } from './patients.actions';
import { PatientsServices } from './patients.services';

@Injectable()
export class PatientsEffects {
    constructor(
        private actions$: Actions,
        private patientsServices: PatientsServices,
        private appointmentListService: AppointmentListService,
        private modalService: BsModalService) {
    }

    searchPatient$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.searchPatient),
            switchMap((action: any) =>
                this.patientsServices.searchPatient(action.searchPattern)
                    .pipe(
                        map((patients: Array<any>) =>
                            PatientsActions.searchPatientSuccess({patients})
                        ),
                        catchError(error =>
                            of(PatientsActions.searchPatientFail({error}))
                        )
                    )
            )
        )
    );


    searchPatientByPetName$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.searchPatientByPetName),
            switchMap((action: any) =>
                this.patientsServices.searchPatientByPetName(action.searchPattern)
                    .pipe(
                        map((patients: Array<any>) =>
                            PatientsActions.searchPatientSuccess({patients})
                        ),
                        catchError(error =>
                            of(PatientsActions.searchPatientFail({error}))
                        )
                    )
            )
        )
    );

    getSelectedPatient: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.getSelectedPatient),
            switchMap((action: any) =>
                this.patientsServices.getPatient(action.personId)
                    .pipe(
                        map((selectedPatient: any) =>
                            PatientsActions.getSelectedPatientSuccess({selectedPatient})
                        ),
                        catchError(error =>
                            of(PatientsActions.getSelectedPatientFail({error}))
                        )
                    )
            )
        )
    );

    getPatientByRemoteId$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.getPatientByRemoteId),
            switchMap((action) =>
                this.patientsServices.getPatientByRemoteId(action.remotePatientId)
                    .pipe(
                        map((selectedPatient: any) =>
                            PatientsActions.getPatientByRemoteIdSuccess({selectedPatient})
                        ),
                        catchError(error =>
                            of(PatientsActions.getPatientByRemoteIdFail({error}))
                        )
                    )
            )
        )
    );

    setPatientStrategy$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.setPatientStrategy),
            mergeMap(({estimated, patientId, strategyId}) =>
                this.patientsServices.setPatientStrategy(patientId, strategyId, estimated)
                    .pipe(
                        map((payload: any) => {
                                if (estimated) {
                                    const dialogMessage = `This mapping will affect ${payload.estimatedAffected} appointments.
                                    Are you sure you want to make this change?`;

                                    return PatientsActions.openConfirmDialog({
                                        dialogMessage,
                                        patientId, strategyId,
                                        okLabel: 'Yes', cancelLabel: 'No'
                                    });
                                }

                                return PatientsActions.getSelectedPatient({personId: patientId});
                            }
                        ),
                        catchError(error =>
                            of(PatientsActions.setPatientStrategyFail({error, patientId, strategyId}))
                        )
                    )
            )
        )
    );

    setPatientStrategyFail$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.setPatientStrategyFail),
            map(({patientId, strategyId}) =>
                PatientsActions.openConfirmDialog({
                    dialogMessage: 'Saving Person Strategy failed. Do you want to retry?',
                    patientId, strategyId,
                    okLabel: 'Retry', cancelLabel: 'Cancel'
                }))
        )
    );

    openConfirmDialog$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(PatientsActions.openConfirmDialog),
                switchMap(({dialogMessage, patientId, strategyId, okLabel, cancelLabel}) => {
                    const message = dialogMessage;
                    const title = 'Confirm';
                    const data: any = { patientId , strategyId };
                    const modalRef = this.modalService
                        .show(BsModalConfirmComponent, createConfirmModalOptions(title, message, okLabel, cancelLabel, data));

                    return modalRef.content.onClose;
                }),
                map((dialogResult: ConfirmDialogResult) => { // map dialog result into action (map function returns observable to action)
                    if (dialogResult.isConfirmed) {
                        return PatientsActions
                            .setPatientStrategy({patientId: dialogResult.data.patientId,
                                                 strategyId: dialogResult.data.strategyId, estimated: false });
                    }

                    return PatientsActions
                        .openConfirmDialogCancel();
                })
            )
    );

    openPatientSettings$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(PatientsActions.openPatientSettings),
                switchMap((action: any) => {
                    const modalRef = this.modalService
                        .show(AppointmentSettingsComponent,
                            {
                                initialState: {
                                    okLabel: 'Save',
                                    cancelLabel: 'Close'
                                },
                                backdrop: 'static',
                                class: 'modal-lg',
                                keyboard: false // disable esc key
                            });

                    return modalRef.content.onClose;
                }),
                map((dialogResult: ConfirmDialogResult) => { // map dialog result into action (map function returns observable to action)
                    if (dialogResult.isConfirmed) {
                        // Used cancelled saving
                        return PatientsActions.openPatientSettingCancel();
                    }

                })
            )
    );

    getPatientSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PatientsActions.getPatientSettings),
            switchMap(({userId}) =>
                this.appointmentListService.getAppointmentSettings(userId)
                    .pipe(
                        map((appointment: any) =>
                            PatientsActions
                                .getPatientSettingsSuccess({displayTwoway: appointment.displayTwoway,
                                                            displayBirthday: appointment.displayBirthday,
                                                            displayIncorrectDestinator: appointment.displayIncorrectDestinator,
                                                            showProviders: appointment.showProviders})
                        )
                    )
            )
        )
    );

    openSubscriptionsDialogFromAngularJS$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(PatientsActions.openSubscriptionsDialogFromLogs),
                map(({selectedDestinator, selectedPatient}) => {
                    this.modalService
                        .show(SubscriptionsDialogComponent,
                            {
                                initialState: {
                                    selectedDestinator,
                                    selectedPatient
                                },
                                backdrop: 'static',
                                class: 'modal-lg',
                                keyboard: false // disable esc key
                            });
                })),
                { dispatch: false }
    );
}
